import type { CountryCode } from './types';
import { getCities, getCityById, getEventsForCity, getLocalEventCountForCity, getMinEventsThreshold, getCountryByCode } from './data-source';
import { SITE_NAME } from './brand';
import { YEAR, getCountryNameEntry, getPhraseEntry, getCityNameEntry, getTemplateStr, getLanguagesForCountry, getLangLabel, getLangLabelEn } from './i18n-helpers';
import { buildLocalizedSlug, buildLocalizedDisplay } from './slug-word-order';
import { asciiCitySlugSubject } from './city-slug-ascii';
import { getSlugForCountry } from './slug-country';

export interface CitySlugEntry {
  cityId: string;
  lang: string;
  slug: string;
  pageTitle: string;
  pageDescription: string;
  h2Title: string;
}

function buildCitySlug(cityId: string, lang: string): string {
  const names = getCityNameEntry(cityId, lang);
  // Some per-language i18n_city_names.slug values are native-script (Arabic,
  // Devanagari, CJK) — those slugs get advertised but 404 at resolution
  // (almanak-qqq9). Force ASCII from the city's romanized name; keep the id as
  // a last-resort ASCII guard.
  const city = getCityById(cityId);
  const romanized = asciiCitySlugSubject(names?.slug ?? cityId, city?.asciiName || city?.name || cityId);
  const citySlug = romanized || cityId;
  const phrase = getPhraseEntry('city_events', lang);
  return buildLocalizedSlug({ subject: citySlug, phrase: phrase.slug, year: String(YEAR) }, lang);
}

function buildCityEntry(cityId: string, cityName: string, countryName: string, lang: string, eventCount: number): CitySlugEntry {
  const names = getCityNameEntry(cityId, lang);
  const cityDisplay = names?.display ?? cityName;
  const phrase = getPhraseEntry('city_events', lang);
  const slug = buildCitySlug(cityId, lang);

  const city = getCityById(cityId);
  const countryCode = city?.country as CountryCode | undefined;
  const countryDisplay = countryCode ? getCountryNameEntry(countryCode, lang).display : countryName;

  const pageTitle = `${buildLocalizedDisplay({ subject: cityDisplay, phrase: phrase.display, year: YEAR }, lang)} | ${SITE_NAME}`;

  return {
    cityId,
    lang,
    slug,
    pageTitle,
    pageDescription: getTemplateStr('city_desc', lang, { city: cityDisplay, country: countryDisplay, count: eventCount, year: YEAR }),
    h2Title: getTemplateStr('city_h2', lang, { city: cityDisplay, phrase: phrase.display, year: YEAR }),
  };
}

// --- City registry cache ---

let _citySlugCache: Record<string, { cityId: string; lang: string }> | null = null;
let _cityReverseCache: Record<string, string> | null = null;
// Slugs of curated cities that no longer generate a page (zero genuine local
// events) mapped to their country page — so a previously-indexed stub URL 301s
// to the country instead of 404ing. Refs: almanak-aqep.
let _cityDropRedirects: Record<string, string> | null = null;

export function ensureCityBuilt() {
  if (_citySlugCache) return;

  const cities = getCities();
  const cache: Record<string, { cityId: string; lang: string }> = {};
  const reverse: Record<string, string> = {};
  const dropped: Record<string, string> = {};

  for (const city of cities) {
    // Only generate pages for cities that have i18n entries (curated set)
    if (!getCityNameEntry(city.id, 'en')) continue;
    if (city.isDisplayed === false) continue;
    const country = getCountryByCode(city.country);
    // Skip cities whose parent country is not displayed (no country page exists)
    if (!country?.isDisplayed) continue;
    const countryName = country?.name ?? '';
    // Gate on GENUINE local events (un-padded). getEventsForCity() pads thin
    // cities with national holidays, which kept ~656 zero-local stub pages alive
    // as duplicates of the country page. The page still renders the padded set,
    // so keep that count for display. Refs: almanak-aqep.
    if (getLocalEventCountForCity(city.id) < getMinEventsThreshold('city')) {
      // Curated-but-dropped: record each language's would-be slug -> the country
      // page in the SAME language. Languages whose city phrase falls back to
      // English collide on the base slug, so append -{lang} exactly as the kept
      // path does (below) to keep each slug language-consistent.
      const seen = new Set<string>();
      for (const lang of getLanguagesForCountry(city.country as CountryCode)) {
        let slug = buildCitySlug(city.id, lang);
        if (seen.has(slug)) slug = `${slug}-${lang}`;
        seen.add(slug);
        const dest = getSlugForCountry(city.country as CountryCode, lang);
        if (slug && dest && slug !== dest) dropped[slug] = dest;
      }
      continue;
    }
    const eventCount = getEventsForCity(city.id).length;

    const langs = getLanguagesForCountry(city.country as CountryCode);

    for (const lang of langs) {
      const entry = buildCityEntry(city.id, city.name, countryName, lang, eventCount);
      if (cache[entry.slug]) {
        entry.slug = `${entry.slug}-${lang}`;
      }
      cache[entry.slug] = { cityId: city.id, lang };
      reverse[`${city.id}:${lang}`] = entry.slug;
    }
  }

  // Don't cache empty — preloads may not have completed yet, see
  // slug-country.ts for the same guard.
  if (Object.keys(cache).length === 0) return;

  // A kept city always wins a slug collision over a dropped one.
  for (const slug of Object.keys(dropped)) {
    if (cache[slug]) delete dropped[slug];
  }
  _cityDropRedirects = dropped;

  _citySlugCache = cache;
  _cityReverseCache = reverse;
}

// --- Public API ---

export function getCitySlugForId(cityId: string, lang: string = 'en'): string {
  ensureCityBuilt();
  return _cityReverseCache?.[`${cityId}:${lang}`] || _cityReverseCache?.[`${cityId}:en`] || '';
}

/**
 * If `slug` is a dropped curated-city page (zero genuine local events), return
 * the country-page slug it should 301 to; otherwise null. Refs: almanak-aqep.
 */
export function getCityDropRedirect(slug: string): string | null {
  ensureCityBuilt();
  return _cityDropRedirects?.[slug] ?? null;
}

export function getCitySlugEntry(slug: string): CitySlugEntry | null {
  ensureCityBuilt();
  const match = _citySlugCache?.[slug];
  if (!match) return null;

  const city = getCityById(match.cityId);
  if (!city) return null;

  const country = getCountryByCode(city.country);
  const countryName = country?.name ?? '';
  const eventCount = getEventsForCity(city.id).length;
  const entry = buildCityEntry(city.id, city.name, countryName, match.lang, eventCount);
  entry.slug = slug;
  return entry;
}

export function getAllCitySlugs(): string[] {
  ensureCityBuilt();
  return Object.keys(_citySlugCache ?? {});
}

export function getAlternateCityLanguages(cityId: string): Array<{ lang: string; slug: string; label: string }> {
  ensureCityBuilt();
  const city = getCityById(cityId);
  if (!city) return [];
  const langs = getLanguagesForCountry(city.country as CountryCode);
  return langs
    .map(lang => ({ lang, slug: _cityReverseCache?.[`${cityId}:${lang}`] ?? '', label: getLangLabel(lang), labelEn: getLangLabelEn(lang) }))
    .filter(x => x.slug);
}

/** Internal: access the raw city slug cache for cross-registry functions */
export function _getCitySlugCache(): Record<string, { cityId: string; lang: string }> {
  ensureCityBuilt();
  return _citySlugCache ?? {};
}
